import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Clock } from "lucide-react";
import { api, trackEvent } from "@/lib/apiClient";
import Layout, { useSEO } from "@/components/Layout";
import { Section, Overline, Button, Divider } from "@/components/primitives";
import { ArticleCard } from "@/components/cards";
import { NewsletterForm } from "@/components/forms";

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [book, setBook] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setArticle(null); setNotFound(false);
    api.get(`/articles/${slug}`)
      .then((r) => {
        setArticle(r.data);
        if (r.data.related_book) api.get(`/books/${r.data.related_book}`).then((b) => setBook(b.data)).catch(() => {});
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  useSEO({
    title: article ? (article.seo_title || `${article.title} | Impactful Mindset`) : "Article",
    description: article?.seo_description || article?.excerpt,
    image: article?.social_image || article?.featured_image,
  });

  useEffect(() => {
    if (article) {
      const ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.text = JSON.stringify({
        "@context": "https://schema.org", "@type": "Article",
        headline: article.title, description: article.excerpt,
        image: article.featured_image, datePublished: article.publication_date,
        author: { "@type": "Person", name: article.author },
        publisher: { "@type": "Organization", name: "Impactful Mindset" },
      });
      document.head.appendChild(ld);
      return () => { document.head.removeChild(ld); };
    }
  }, [article]);

  if (notFound)
    return <Layout><Section className="py-32 text-center"><h1 className="font-serif-display text-3xl">Article not found</h1><Button to="/ideas" className="mt-6">Back to Ideas</Button></Section></Layout>;

  if (!article)
    return <Layout><Section className="py-32"><div className="h-96 bg-slate-100 animate-pulse max-w-3xl mx-auto" /></Section></Layout>;

  const date = article.publication_date ? new Date(article.publication_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "";

  return (
    <Layout>
      <article>
        <Section className="pt-16 pb-8">
          <div className="max-w-3xl mx-auto text-center">
            <Link to={`/ideas/${article.category?.toLowerCase().replace(/\s/g, "-")}`}><Overline>{article.category}</Overline></Link>
            <h1 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-bold mt-4 leading-tight text-[#111827]">{article.title}</h1>
            {article.subtitle && <p className="font-serif-display italic text-xl text-slate-500 mt-4">{article.subtitle}</p>}
            <div className="flex items-center justify-center gap-4 mt-6 text-sm text-slate-500">
              <span>{article.author}</span><span>·</span><span>{date}</span><span>·</span>
              <span className="inline-flex items-center gap-1"><Clock size={13} /> {article.reading_time} min read</span>
            </div>
          </div>
        </Section>

        {article.featured_image && (
          <div className="max-w-4xl mx-auto px-5 sm:px-8">
            <div className="aspect-[16/9] overflow-hidden bg-slate-100 border border-slate-200">
              <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        <Section className="py-14">
          <div className="im-prose mx-auto" dangerouslySetInnerHTML={{ __html: article.body }} />
        </Section>

        <Section className="pb-8">
          <div className="max-w-3xl mx-auto space-y-6">
            {article.reflection_question && (
              <div className="border border-slate-200 bg-[#F8F9FA] p-7">
                <Overline>Think About It</Overline>
                <p className="font-serif-display text-xl text-[#111827] mt-3 leading-snug">{article.reflection_question}</p>
              </div>
            )}
            {article.practical_application && (
              <div className="border-l-2 border-[#0F2C59] bg-[#F8F9FA] p-7">
                <Overline>Apply It</Overline>
                <p className="text-slate-700 mt-3 leading-relaxed">{article.practical_application}</p>
              </div>
            )}
          </div>
        </Section>

        {article._related?.length > 0 && (
          <Section className="py-14">
            <div className="max-w-5xl mx-auto">
              <Overline>Related Ideas</Overline>
              <h2 className="font-serif-display text-2xl font-semibold mt-2 mb-8 text-[#111827]">Go deeper</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {article._related.map((a) => <ArticleCard key={a.id} article={a} />)}
              </div>
            </div>
          </Section>
        )}

        {book && (
          <Section className="py-14 bg-[#F8F9FA]">
            <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8 items-center">
              <div className="aspect-[3/4] bg-white border border-slate-200 overflow-hidden max-w-[180px]">
                <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover" />
              </div>
              <div className="md:col-span-2">
                <Overline>Go Deeper</Overline>
                <h3 className="font-serif-display text-2xl font-semibold mt-2 text-[#111827]">{book.title}</h3>
                <p className="text-slate-600 mt-3 leading-relaxed">{book.short_description}</p>
                <Button to={`/books/${book.slug}`} variant="secondary" className="mt-5" data-testid="article-book-cta"
                  onClick={() => trackEvent("book_cta_click", { book: book.slug })}>View the Book</Button>
              </div>
            </div>
          </Section>
        )}

        <Section className="py-16">
          <div className="max-w-3xl mx-auto bg-[#0F2C59] p-10 text-white text-center">
            <h2 className="font-serif-display text-2xl sm:text-3xl font-semibold">Join Impactful Mindset</h2>
            <p className="text-slate-200 mt-3">Ideas worth thinking about, delivered to your inbox.</p>
            <div className="mt-7 flex justify-center"><NewsletterForm dark testidPrefix="article-newsletter" /></div>
          </div>
        </Section>
      </article>
    </Layout>
  );
}
